import { FormikProvider, useFormik } from 'formik';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ArrowRightIcon,
	Button,
	Checkbox,
	FlexGap,
	GraphQLErrorMessage,
	Label,
	Modal,
	NumericInput,
	Paragraph,
	Popover,
	Select,
	TextArea,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import { formatSalary } from './formatSalary';

const REASONS = [
	graphql.SalaryChangeReason.PeriodicStep,
	graphql.SalaryChangeReason.Performance,
	graphql.SalaryChangeReason.Promotion,
	graphql.SalaryChangeReason.MarketCorrection,
	graphql.SalaryChangeReason.CollectiveAgreement,
	graphql.SalaryChangeReason.OneOffBonus,
];

const BASES = [
	graphql.SalaryChangeBasis.Performance,
	graphql.SalaryChangeBasis.Skills,
	graphql.SalaryChangeBasis.Responsibility,
	graphql.SalaryChangeBasis.MarketPosition,
];

export default function CreateSalaryChangeModal(props: {
	userID: string;
	currentAmount?: number;
	salaryScale?: { name: string; startAmount: number; endAmount: number } | null;
	onClose: () => void;
	onCreated: () => void;
}) {
	const { t, i18n } = useTranslation();
	const { addToast } = useToasts();
	const [error, attempt] = useErrorBoundary();
	const [create, { loading }] = graphql.useCreateSalaryChangeMutation();
	const newSalaryInputId = useId();

	const formik = useFormik({
		initialValues: {
			amount: props.currentAmount != null ? String(props.currentAmount) : '',
			fteHours: '',
			reason: '' as graphql.SalaryChangeReason | '',
			bases: [] as graphql.SalaryChangeBasis[],
			explanation: '',
		},
		onSubmit: attempt(async (values) => {
			const amount = Number(values.amount);
			const fteHours = values.fteHours === '' ? undefined : Number(values.fteHours);
			if (!values.reason || !Number.isInteger(amount)) {
				return;
			}

			if (fteHours != null && !Number.isInteger(fteHours)) {
				return;
			}

			await create({
				variables: {
					input: {
						userID: props.userID,
						amount,
						fteHours,
						reason: values.reason,
						bases: values.bases,
						explanation: values.explanation.trim() || null,
					},
				},
			});

			addToast(t('salaryDevelopment.saved'), { appearance: 'success', autoDismiss: true });
			props.onCreated();
			props.onClose();
		}),
	});

	const salaryScaleLabel = props.salaryScale
		? t('salaryDevelopment.form.salaryScale', {
				name: props.salaryScale.name,
				start: formatSalary(props.salaryScale.startAmount, i18n.language),
				end: formatSalary(props.salaryScale.endAmount, i18n.language),
			})
		: undefined;
	const amount = Number(formik.values.amount);
	const fteHours = formik.values.fteHours === '' ? undefined : Number(formik.values.fteHours);
	const fteHoursValid =
		fteHours == null || (Number.isInteger(fteHours) && fteHours >= 1 && fteHours <= 80);
	const canSave =
		Number.isInteger(amount) &&
		amount >= 1 &&
		fteHoursValid &&
		formik.values.reason !== '' &&
		formik.values.bases.length > 0 &&
		!formik.isSubmitting &&
		!loading;

	return (
		<Popover center visible onDismiss={props.onClose}>
			<Modal>
				<Modal.Header title={t('salaryDevelopment.add')} onClose={props.onClose} />
				<FormikProvider value={formik}>
					<Modal.Contents>
						<FlexGap flexDirection="column" gap={tokens.gap.group}>
							<FlexGap alignItems="center" gap={tokens.gap.element}>
								{(props.currentAmount != null || salaryScaleLabel) && (
									<>
										<FlexGap
											flexDirection="column"
											gap={tokens.gap.inline}
											flexShrink={1}
											minWidth={0}
										>
											{props.currentAmount != null && (
												<Label style={{ whiteSpace: 'nowrap' }}>
													{t('salaryDevelopment.form.currentSalary')}:{' '}
													{formatSalary(props.currentAmount, i18n.language)}
												</Label>
											)}
											{salaryScaleLabel && <Paragraph small>{salaryScaleLabel}</Paragraph>}
										</FlexGap>
										<Box color={tokens.icon.secondary} flexShrink={0}>
											<ArrowRightIcon size={16} />
										</Box>
									</>
								)}
								<Box flex="1 1 140px" minWidth={140}>
									<NumericInput
										id={newSalaryInputId}
										prefix="€"
										value={formik.values.amount}
										onChange={(value) => formik.setFieldValue('amount', value ?? '')}
									/>
								</Box>
								<Label
									as="label"
									htmlFor={newSalaryInputId}
									style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
								>
									{t('salaryDevelopment.form.newSalary')}
								</Label>
							</FlexGap>
							<Box width={220}>
								<NumericInput
									label={t('salaryDevelopment.form.fteHours')}
									value={formik.values.fteHours}
									onChange={(value) => formik.setFieldValue('fteHours', value ?? '')}
								/>
							</Box>
							<Box>
								<Label mb={2}>{t('salaryDevelopment.form.reason')}</Label>
								<Select
									search={false}
									role="combobox"
									aria-label={t('salaryDevelopment.form.reason')}
									placeholder={t('salaryDevelopment.form.reasonPlaceholder')}
									selectedValue={formik.values.reason || undefined}
									options={REASONS.map((reason) => ({
										value: reason,
										title: t(`salaryDevelopment.reason.${reason}`),
									}))}
									onChange={(value) => formik.setFieldValue('reason', value ?? '')}
								/>
							</Box>
							<Box>
								<Label mb={1}>{t('salaryDevelopment.form.bases')}</Label>
								<FlexGap flexDirection="column" gap={tokens.gap.inline}>
									{BASES.map((basis) => (
										<Checkbox
											key={basis}
											aria-label={t(`salaryDevelopment.basis.${basis}`)}
											checked={formik.values.bases.includes(basis)}
											label={t(`salaryDevelopment.basis.${basis}`)}
											onClick={() => {
												const selected = formik.values.bases.includes(basis)
													? formik.values.bases.filter((item) => item !== basis)
													: [...formik.values.bases, basis];
												formik.setFieldValue('bases', selected);
											}}
										/>
									))}
								</FlexGap>
							</Box>
							<Box>
								<Label mb={2}>{t('salaryDevelopment.form.explanation')}</Label>
								<TextArea
									minRows={3}
									aria-label={t('salaryDevelopment.form.explanation')}
									placeholder={t('salaryDevelopment.form.explanationPlaceholder')}
									value={formik.values.explanation}
									onChange={(text) => formik.setFieldValue('explanation', text)}
								/>
							</Box>
							<GraphQLErrorMessage error={error} />
						</FlexGap>
					</Modal.Contents>
					<Modal.Footer>
						<Button
							type="secondary"
							role="button"
							aria-label={t('modal.footer.form.cancel')}
							title={t('modal.footer.form.cancel')}
							onClick={props.onClose}
						/>
						<Button
							type="primary"
							role="button"
							aria-label={t('common.save')}
							title={t('common.save')}
							disabled={!canSave}
							onClick={() => formik.submitForm()}
						/>
					</Modal.Footer>
				</FormikProvider>
			</Modal>
		</Popover>
	);
}
